import type { ReactNode } from "react";
import { ApiError } from "@/api/client";
import { cn } from "@/lib/utils";
import { Button } from "./button";

/** Resposta perdida num salvamento: o pedido saiu e pode ter sido gravado (P5). */
export const UNCONFIRMED_SAVE_MESSAGE =
  "Não foi possível confirmar o salvamento. Confira em Lançamentos antes de tentar de novo.";

/**
 * Erro de API/rede em destaque (role="alert"); não renderiza nada sem erro. Em `saving` (salvar lançamento ou
 * transferência), a falha de rede durante o envio não é "nada foi salvo": pede para conferir antes de repetir.
 */
export function ErrorBanner({ error, saving = false }: { error: unknown; saving?: boolean }) {
  if (!error) return null;
  const message =
    saving && error instanceof ApiError && error.code === "network_error"
      ? UNCONFIRMED_SAVE_MESSAGE
      : error instanceof ApiError || error instanceof Error
        ? error.message
        : "Algo deu errado.";
  return (
    <p role="alert" className="m-0 rounded-control bg-expense-soft px-3 py-2 text-sm text-expense">
      {message}
    </p>
  );
}

/** Aviso antes de reenviar com conteúdo mudado depois de uma resposta perdida (P1). */
export const RESEND_WARNING_MESSAGE =
  "O salvamento anterior pode ter chegado. Confira em Lançamentos para não salvar em dobro.";

export function ResendWarning({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) {
  return (
    <div
      role="status"
      className="flex flex-col gap-3 rounded-control bg-warning-soft px-3 py-3 text-sm text-warning"
    >
      <p className="m-0">{RESEND_WARNING_MESSAGE}</p>
      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={onConfirm}>
          Salvar mesmo assim
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Voltar
        </Button>
      </div>
    </div>
  );
}

export function Notice({ children }: { children: ReactNode }) {
  return (
    <p role="status" className="m-0 rounded-control bg-warning-soft px-3 py-2 text-sm text-warning">
      {children}
    </p>
  );
}

/** Bloco cinza no formato do conteúdo que ainda vai chegar. */
export function Skeleton({ className }: { className?: string }) {
  return (
    <span aria-hidden="true" className={cn("block animate-pulse rounded-control bg-surface-2", className)} />
  );
}

/** Carregando: skeletons em linhas, com o texto só para leitores de tela. */
export function Loading({ label = "Carregando…", rows = 3 }: { label?: string; rows?: number }) {
  return (
    <div role="status" aria-live="polite" className="flex flex-col gap-3 py-2">
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: linhas estáticas
        <Skeleton key={i} className={cn("h-4", i % 2 ? "w-2/3" : "w-full")} />
      ))}
    </div>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="rounded-card border border-dashed border-border p-8 text-center">
      <p className="m-0 font-medium text-ink">{title}</p>
      {children && <div className="mt-2 text-sm text-muted">{children}</div>}
    </div>
  );
}
